/**
 * LocalVideoProgressBar
 *
 * Simplified variant of VideoProgressBar for locally-controlled <video> elements
 * (e.g. pre-upload preview). Unlike VideoProgressBar:
 * - Takes a ref to the video element directly — no videoElementRegistry.
 * - No typeLimit — single-color progress track.
 * - Seek is applied directly to the video element; optional onSeek callback.
 */

import React, { useRef, useEffect } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';

export interface LocalVideoProgressBarProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  duration: number;
  onSeek?: (time: number) => void;
  className?: string;
}

export const LocalVideoProgressBar: React.FC<LocalVideoProgressBarProps> = ({
  videoRef,
  duration,
  onSeek,
  className = '',
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const progressBarRef = useRef<HTMLDivElement>(null);
  const rangeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || duration <= 0) return;

    let animationFrameId = 0;
    let lastUpdateTime = 0;
    const UPDATE_INTERVAL = 100;

    const updateProgress = (timestamp: number, forceUpdate = false) => {
      const shouldUpdate = forceUpdate ||
        (!video.paused && timestamp - lastUpdateTime >= UPDATE_INTERVAL);

      if (shouldUpdate) {
        const progressPercent = (video.currentTime / duration) * 100;
        if (progressBarRef.current) {
          progressBarRef.current.style.width = `${progressPercent}%`;
        }
        if (rangeInputRef.current) {
          rangeInputRef.current.value = video.currentTime.toString();
        }
        lastUpdateTime = timestamp;
      }

      if (!video.paused) {
        animationFrameId = requestAnimationFrame(updateProgress);
      }
    };

    const startLoop = () => {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(updateProgress);
    };

    const onSeeked = () => updateProgress(performance.now(), true);

    video.addEventListener('play', startLoop);
    video.addEventListener('seeked', onSeeked);

    updateProgress(0, true);

    return () => {
      cancelAnimationFrame(animationFrameId);
      video.removeEventListener('play', startLoop);
      video.removeEventListener('seeked', onSeeked);
    };
  }, [videoRef, duration]);

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    const video = videoRef.current;
    if (video) video.currentTime = newTime;
    onSeek?.(newTime);
  };

  return (
    <div className={`w-full ${className}`}>
      <div className="relative" style={{ height: `${responsive.progressBar.height}px` }}>
        <div
          className="absolute inset-0 rounded-full"
          style={{
            height: `${responsive.progressBar.height}px`,
            backgroundColor: theme.disabled,
          }}
        />

        <div
          ref={progressBarRef}
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            height: `${responsive.progressBar.height}px`,
            width: '0%',
            backgroundColor: theme.activeRed,
            transition: 'width 100ms linear',
            willChange: 'width',
          }}
        />

        <input
          ref={rangeInputRef}
          type="range"
          min={0}
          max={duration || 0}
          step="any"
          defaultValue={0}
          onChange={handleProgressChange}
          className="absolute inset-0 w-full opacity-0 cursor-pointer"
          style={{ height: `${responsive.progressBar.height}px`, zIndex: 10 }}
        />
      </div>
    </div>
  );
};
